import { personalAccessToken } from '@/services/accessToken';
import { DATASET_SYNC_RECOVERY_EVENT } from '@/local-sync/datasetSync';

/**
 * How often a count the tab carries is re-read while the reader is looking at the window (I-0635).
 */
export const FOCUSED_POLL_INTERVAL_MS = 30_000;

/**
 * How often it is re-read while the tab is hidden or the window is not focused (I-0635).
 *
 * A minute, because that is what browsers throttle a hidden tab's timers to anyway: Chrome's
 * intensive throttling aligns a hidden page's timer chain to once a minute, and Safari is at least as
 * strict. Asking for more often buys nothing there; asking for less would leave the tab title behind
 * the sidebar of the window next to it.
 */
export const BACKGROUND_POLL_INTERVAL_MS = 60_000;

/**
 * How long a push waits before the read it asks for (I-0635, AC-2).
 *
 * A doorbell rings in bursts — filing a decision creates the item and then asks its question in a
 * second write, and the item's event arrives before the question exists. One second lets the burst
 * land and turns it into one read, not three, and still reads as "at once" to a person.
 */
export const PUSH_SETTLE_MS = 1_000;

/**
 * F-0236: joins the signed-in person's attention stream (`local-sync/myAttention.ts`). Injected, so a
 * store's test hands in a fake instead of a socket.
 */
export type AttentionSubscriber = (listener: {
  onChange?: (change: { reason: string; unreadCount: number }) => void;
  onResume?: () => void;
}) => () => void;

export interface AttentionPollerOptions {
  /** The read. Never run twice at once: a trigger that arrives mid-read queues exactly one more. */
  read: () => unknown;
  focusedIntervalMs?: number;
  backgroundIntervalMs?: number;
}

export interface AttentionPoller {
  /**
   * Ask for a read within `delayMs`. The earlier of this and the pending tick wins, so a burst of
   * asks is one read and a later ask never postpones an earlier one.
   */
  soon(delayMs?: number): void;
  /**
   * A push asks for a read, leading-edge (I-0900): the first push after {@link PUSH_SETTLE_MS} of
   * quiet reads at once, and pushes inside that window coalesce into one trailing read. A burst is
   * still at most two reads, and a lone ring is on screen within the second a person was promised.
   */
  push(): void;
  /** The interval the next scheduled tick uses: focused or background. */
  readonly intervalMs: number;
  dispose(): void;
}

/**
 * One re-read loop for a count that has to stay current in a tab nobody is looking at (I-0635).
 *
 * `DecisionsStore` and `NotificationStore` each used to own a `setInterval` that stopped on `blur`
 * and was gated on `windowFocused`. So a tab in the background — or merely behind another window —
 * never re-read, and its title count stayed frozen until it was activated. Jay saw it in Safari; it
 * is the same in every browser, because it was the app that stopped asking.
 *
 * What this does instead:
 *
 * - **re-reads every {@link FOCUSED_POLL_INTERVAL_MS} while the window is focused and visible, and
 *   every {@link BACKGROUND_POLL_INTERVAL_MS} otherwise.** Hidden or unfocused slows the loop; it
 *   never stops it (AC-1);
 * - **re-reads at once when the reader comes back**: `visibilitychange` to visible, `focus`, and
 *   `pageshow` with `persisted` — the last is Safari's back-forward cache restoring a page whose
 *   timers were frozen, where neither of the other two can be relied on to fire (AC-3);
 * - **re-reads at once on the shell's own resume signal**, `DATASET_SYNC_RECOVERY_EVENT` (I-1077).
 *   Inside the native iOS shell, backgrounding and foregrounding the app does not reliably fire
 *   `visibilitychange` or `focus` on the web page at all — `WKWebView` does not tie the page's
 *   visibility state to the app's own UIKit lifecycle, so none of the three triggers above can be
 *   counted on there. `ShellRuntime.tsx` already turns the shell's `foreground` and
 *   `connectivity { online: true }` messages into this one DOM event for exactly that reason
 *   (`useRefetchOnServiceRecovery` reads it too); before this, nothing in this shared loop did,
 *   so a count that went stale during an outage or a long background spell stayed stale — with no
 *   error, because no new read was ever asked for — until something else happened to trigger one;
 * - **exposes {@link AttentionPoller.soon}** so a push channel can ask for a read without owning a
 *   timer of its own (AC-2).
 *
 * ⛔ **Exactly one timer, by construction** (AC-4). It is a chain of `setTimeout`s held in one
 * slot, not a `setInterval` plus extras: a cadence change, a push and a return to the tab all
 * reschedule the same slot, so nothing can stack. A read in flight is never joined by a second one
 * — a trigger that arrives meanwhile queues one more read for when it finishes, which is what a push
 * that raced a read needs and all it needs.
 *
 * ⚠️ **The honest limit.** Safari suspends a tab that stays hidden for long, and other browsers
 * freeze or discard background tabs under memory pressure. No script runs in such a tab — no timer,
 * no socket — so its title is as old as the moment it was suspended. It catches up the instant it is
 * shown again, through the visible/`pageshow`/`focus` reads above; nothing a page does can make it
 * catch up sooner.
 */
export function createAttentionPoller({
  read,
  focusedIntervalMs = FOCUSED_POLL_INTERVAL_MS,
  backgroundIntervalMs = BACKGROUND_POLL_INTERVAL_MS,
}: AttentionPollerOptions): AttentionPoller {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let dueAt = 0;
  let inFlight = false;
  let readAgain = false;
  let disposed = false;
  let focused = typeof document.hasFocus === 'function' ? document.hasFocus() : true;

  const visible = () => document.visibilityState !== 'hidden';
  const intervalMs = () => (focused && visible() ? focusedIntervalMs : backgroundIntervalMs);
  // I-0942: a tab with no stored credential — signed out, or on a public token route (the reporter
  // page, an invitation) that never has one — has nothing this loop may ask for. Rescheduling rather
  // than stopping keeps it ready the moment a credential is written, with no separate "start" call.
  // A browser that refuses storage (a private window, blocked site data) throws on the read; that
  // is no credential this loop can see, never an error escaping its timer.
  const hasCredential = () => {
    try {
      return Boolean(personalAccessToken());
    } catch {
      return false;
    }
  };

  const clear = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };

  const schedule = (delayMs: number, keepEarlier: boolean) => {
    if (disposed) return;
    const at = Date.now() + delayMs;
    if (keepEarlier && timer !== null && dueAt <= at) return;
    clear();
    dueAt = at;
    timer = setTimeout(tick, delayMs);
  };

  const tick = () => {
    timer = null;
    if (disposed) return;
    if (inFlight) {
      readAgain = true;
      return;
    }
    if (!hasCredential()) {
      schedule(intervalMs(), true);
      return;
    }
    inFlight = true;
    Promise.resolve()
      .then(read)
      .catch(() => undefined)
      .finally(() => {
        inFlight = false;
        if (readAgain) {
          readAgain = false;
          schedule(0, true);
        } else {
          schedule(intervalMs(), true);
        }
      });
  };

  const soon = (delayMs = 0) => {
    if (inFlight) {
      readAgain = true;
      return;
    }
    schedule(delayMs, true);
  };

  let lastPushAt = Number.NEGATIVE_INFINITY;
  const push = () => {
    const now = Date.now();
    const quiet = now - lastPushAt >= PUSH_SETTLE_MS;
    lastPushAt = now;
    soon(quiet ? 0 : PUSH_SETTLE_MS);
  };

  const handleFocus = () => {
    focused = true;
    soon(0);
  };
  const handleBlur = () => {
    // Slower, never stopped. The pending tick stays where it is; the one after it takes the
    // background interval.
    focused = false;
  };
  const handleVisibilityChange = () => {
    if (visible()) soon(0);
  };
  const handlePageShow = (event: Event) => {
    if ((event as PageTransitionEvent).persisted) soon(0);
  };
  const handleServiceRecovery = () => soon(0);

  window.addEventListener('focus', handleFocus);
  window.addEventListener('blur', handleBlur);
  window.addEventListener('pageshow', handlePageShow);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener(DATASET_SYNC_RECOVERY_EVENT, handleServiceRecovery);
  schedule(intervalMs(), false);

  return {
    soon,
    push,
    get intervalMs() {
      return intervalMs();
    },
    dispose() {
      disposed = true;
      clear();
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener(DATASET_SYNC_RECOVERY_EVENT, handleServiceRecovery);
    },
  };
}
